import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import {
  MessagesSquare, Plus, Loader2, ArrowLeft, X, Flag, Send,
} from "lucide-react";
import { toast } from "sonner";

/**
 * CommunityForumPanel — Iter77 / Engagement.
 *
 * A per-course (or global, when pathId is null) discussion board.
 * Two states inside one component:
 *   1. List   — recent threads, "Start a thread" button up top.
 *   2. Thread — selected thread with its flat message stream + reply box.
 *
 * Keeping it as one component (not /pages) so it can mount inside
 * BentoPath and Hub without route swaps. Use the back arrow to go
 * from thread → list. URL doesn't change.
 *
 * Anonymous users can read threads; posting requires auth (the
 * backend will 401 — we trap and prompt sign-in).
 */
export default function CommunityForumPanel({ pathId = null, pathGoal = null, currentUserId = null }) {
  const [phase, setPhase] = useState("list"); // list | thread | new
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // {thread, messages}
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [reply, setReply] = useState("");
  // new-thread form
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");

  const loadThreads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/forum/threads", { params: { path_id: pathId || undefined } });
      setThreads(data?.threads || []);
    } catch {
      setThreads([]);
    } finally {
      setLoading(false);
    }
  };

  const loadThread = async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/forum/threads/${id}`);
      setActive(data);
      setPhase("thread");
    } catch {
      toast.error("Couldn't load thread");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadThreads(); /* eslint-disable-next-line */ }, [pathId]);

  const createThread = async () => {
    if (newTitle.trim().length < 4) { toast.error("Give it a title (≥4 chars)"); return; }
    if (!newBody.trim()) { toast.error("Add a starting message"); return; }
    setPosting(true);
    try {
      const { data } = await api.post("/forum/threads", {
        path_id: pathId || null,
        title: newTitle.trim(),
        first_message: newBody.trim(),
      });
      toast.success("Thread posted");
      setNewTitle(""); setNewBody("");
      await loadThread(data.thread_id);
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post");
    } finally {
      setPosting(false);
    }
  };

  const postReply = async () => {
    if (!reply.trim() || !active?.thread) return;
    setPosting(true);
    try {
      await api.post(`/forum/threads/${active.thread.thread_id}/messages`, {
        body: reply.trim(),
      });
      setReply("");
      await loadThread(active.thread.thread_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reply");
    } finally {
      setPosting(false);
    }
  };

  const reportMessage = async (messageId) => {
    try {
      await api.post(`/forum/messages/${messageId}/report`, { reason: "user_reported" });
      toast.success("Reported. Thanks — we'll review.");
    } catch {
      toast.error("Couldn't send report");
    }
  };

  return (
    <section
      className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden"
      data-testid="forum-panel"
    >
      <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {phase === "thread" || phase === "new" ? (
            <button
              type="button"
              onClick={() => { setPhase("list"); setActive(null); }}
              className="h-8 w-8 grid place-items-center rounded-md text-white/55 hover:text-white hover:bg-white/5"
              aria-label="Back to threads"
              data-testid="forum-back"
            >
              <ArrowLeft size={14} />
            </button>
          ) : (
            <div className="h-9 w-9 grid place-items-center rounded-xl bg-[#5D3FD3]/12 border border-[#5D3FD3]/35 shrink-0">
              <MessagesSquare size={14} className="text-[#bcb0ff]" />
            </div>
          )}
          <div className="min-w-0">
            <div className="label-eyebrow text-[#bcb0ff]">
              {pathId ? "Course discussion" : "Community"}
            </div>
            <h3 className="font-display text-base sm:text-lg text-white tracking-tight truncate">
              {phase === "thread" && active?.thread
                ? active.thread.title
                : phase === "new"
                  ? "Start a new thread"
                  : pathGoal || "Recent threads"}
            </h3>
          </div>
        </div>
        {phase === "list" && (
          <button
            type="button"
            onClick={() => setPhase("new")}
            className="text-[11.5px] btn-mint-outline px-3 py-1.5 rounded-full inline-flex items-center gap-1"
            data-testid="forum-new-thread"
          >
            <Plus size={11} /> New thread
          </button>
        )}
      </div>

      {/* LIST */}
      {phase === "list" && (
        <div className="px-5 py-4 space-y-2" data-testid="forum-list">
          {loading && (
            <div className="text-white/45 text-xs flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
          )}
          {!loading && threads.length === 0 && (
            <div className="text-white/45 text-[12.5px] text-center py-4">
              No threads yet. Be the first to start a conversation about <span className="text-white">{pathGoal || "Oak AI Campus"}</span>.
            </div>
          )}
          {threads.map((t) => (
            <button
              key={t.thread_id}
              type="button"
              onClick={() => loadThread(t.thread_id)}
              className="w-full text-left rounded-lg border border-white/8 bg-white/[0.025] hover:bg-white/[0.045] hover:border-white/22 px-3 py-2.5 transition flex items-center gap-3"
              data-testid={`forum-thread-${t.thread_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-display text-[13px] text-white leading-snug line-clamp-1">{t.title}</div>
                <div className="text-[10.5px] text-white/45 font-mono-oak mt-1 flex items-center gap-2 truncate">
                  <span className="truncate">{t.created_by_name}</span>
                  <span>· {t.message_count} {t.message_count === 1 ? "msg" : "msgs"}</span>
                  <span>· {(t.last_message_at || "").slice(0, 10)}</span>
                </div>
              </div>
              <MessagesSquare size={13} className="text-white/30 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* NEW THREAD */}
      {phase === "new" && (
        <div className="px-5 py-4 space-y-3" data-testid="forum-new-form">
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-display mb-1.5">Title</div>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value.slice(0, 140))}
              placeholder={pathGoal ? `Question about ${pathGoal}` : "What's on your mind?"}
              className="w-full text-[13px] px-3 py-2 rounded-lg border border-white/10 bg-white/[0.04] text-white placeholder:text-white/30 focus:outline-none focus:border-white/30"
              data-testid="forum-new-title"
            />
          </label>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-display mb-1.5">Your message</div>
            <textarea
              value={newBody}
              onChange={(e) => setNewBody(e.target.value.slice(0, 2000))}
              rows={4}
              placeholder="Add detail, context, what you've tried so far. Markdown supported."
              className="w-full text-[13px] px-3 py-2 rounded-lg border border-white/10 bg-white/[0.04] text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 resize-none"
              data-testid="forum-new-body"
            />
            <div className="text-[10px] text-white/35 font-mono-oak mt-1 text-right">{newBody.length}/2000</div>
          </label>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => { setPhase("list"); setNewTitle(""); setNewBody(""); }}
              className="text-[12px] text-white/55 hover:text-white px-3 py-2 rounded-md hover:bg-white/5"
              data-testid="forum-new-cancel"
            >
              Cancel
            </button>
            <button
              onClick={createThread}
              disabled={posting || !newTitle.trim() || !newBody.trim()}
              className="btn-primary px-5 py-2 rounded-full font-display font-semibold text-[12.5px] disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="forum-new-submit"
            >
              {posting ? <><Loader2 size={11} className="animate-spin" /> Posting…</> : <><Send size={11} /> Post</>}
            </button>
          </div>
        </div>
      )}

      {/* THREAD VIEW */}
      {phase === "thread" && active?.thread && (
        <div data-testid="forum-thread-view">
          <div className="px-5 py-4 max-h-[400px] overflow-y-auto space-y-3">
            <AnimatePresence initial={false}>
              {(active.messages || []).map((m) => {
                const isMe = currentUserId && m.user_id === currentUserId;
                return (
                  <motion.article
                    key={m.message_id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`rounded-lg border p-3 ${
                      isMe ? "border-[#00FF9D]/22 bg-[#00FF9D]/[0.04]" : "border-white/8 bg-white/[0.02]"
                    }`}
                    data-testid={`forum-message-${m.message_id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      {m.user_picture ? (
                        <img src={m.user_picture} alt="" className="h-7 w-7 rounded-full object-cover border border-white/10 shrink-0" />
                      ) : (
                        <div className="h-7 w-7 rounded-full bg-white/[0.06] border border-white/10 grid place-items-center text-[9px] font-display font-semibold text-white/65 shrink-0">
                          {(m.user_name || "?").slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap text-[10.5px]">
                          <span className="font-display text-white">{m.user_name || "Learner"}</span>
                          {isMe && <span className="text-[#9bffcc] font-mono-oak text-[9px] uppercase tracking-[0.18em]">you</span>}
                          <span className="text-white/35 font-mono-oak">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                        </div>
                        <p className="text-[13px] text-white/85 leading-relaxed mt-1 whitespace-pre-wrap">{m.body}</p>
                      </div>
                      {!isMe && (
                        <button
                          type="button"
                          onClick={() => reportMessage(m.message_id)}
                          className="text-white/30 hover:text-rose-300 shrink-0"
                          aria-label="Report message"
                          data-testid={`forum-report-${m.message_id}`}
                        >
                          <Flag size={11} />
                        </button>
                      )}
                    </div>
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </div>
          <div className="px-5 py-3 border-t border-white/8 flex items-end gap-2">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value.slice(0, 2000))}
              rows={2}
              placeholder="Add to the conversation…"
              className="flex-1 text-[13px] px-3 py-2 rounded-lg border border-white/10 bg-white/[0.04] text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 resize-none"
              data-testid="forum-reply-input"
            />
            <button
              onClick={postReply}
              disabled={posting || !reply.trim()}
              className="btn-primary h-10 px-4 rounded-lg font-display font-semibold text-[12.5px] disabled:opacity-40 inline-flex items-center gap-1.5 shrink-0"
              data-testid="forum-reply-submit"
            >
              {posting ? <Loader2 size={12} className="animate-spin" /> : <><Send size={11} /> Reply</>}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
